<template>
  <!-- Callout de hover del power block (presentación pura). Recibe ya calculado el
       `callout` {name, activity, activityProgress, layers[{code,pct}], anchor, pill, line}
       en coords de pantalla. Overlay que no intercepta el ratón. Layout con Tailwind; el
       acabado fino y las barras en el <style> (Tailwind no lo cubre bien). -->
  <div class="absolute inset-0 pointer-events-none z-[5]">
    <svg class="absolute inset-0 w-full h-full overflow-visible">
      <line :x1="callout.line.x1" :y1="callout.line.y1"
        :x2="callout.line.x2" :y2="callout.line.y2"
        stroke="#3a6bc8" stroke-width="1" stroke-dasharray="4 3" stroke-opacity="0.5"></line>
      <circle :cx="callout.anchor.x" :cy="callout.anchor.y" r="3"
        fill="#ffffff" stroke="#3a6bc8" stroke-width="1.5"></circle>
    </svg>

    <div class="epc-callout" :style="pillStyle">
      <div class="flex items-center gap-2">
        <span class="epc-callout-badge">PB</span>
        <span class="epc-callout-name">{{ callout.name }}</span>
      </div>

      <div v-if="callout.activity" class="epc-callout-activity">
        <span class="epc-callout-activity-label">{{ $t('activity') }}</span>
        <span class="epc-callout-activity-value">{{ callout.activity }}</span>
      </div>

      <!-- Avance de la actividad -->
      <div v-if="callout.activityProgress != null" class="epc-row">
        <div class="epc-bar"><div class="epc-bar-fill" :style="{ width: callout.activityProgress + '%' }"></div></div>
        <span class="epc-pct">{{ callout.activityProgress }}%</span>
      </div>

      <!-- Avance por capa (hincados / estructuras / módulos) -->
      <div v-if="callout.layers && callout.layers.length" class="epc-layers">
        <div v-for="l in callout.layers" :key="l.code" class="epc-layer-row">
          <span class="epc-layer-label">{{ $t(l.code) }}</span>
          <div class="epc-bar epc-bar-sm"><div class="epc-bar-fill" :style="{ width: l.pct + '%' }"></div></div>
          <span class="epc-pct epc-pct-sm">{{ l.pct }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ViewerHoverCallout",

  props: {
    callout: { type: Object, required: true },
  },

  computed: {
    // La píldora se ancla por su borde interior: `left` si va a la derecha del bloque,
    // `right` si va a la izquierda → la línea conecta exacto sin depender del ancho.
    pillStyle() {
      const p = this.callout.pill
      const style = { top: p.top + 'px' }
      if (this.callout.side === 'left') style.right = p.right + 'px'
      else style.left = p.left + 'px'
      return style
    },
  },
}
</script>

<style scoped>
.epc-callout {
  position: absolute;
  min-width: 190px;
  max-width: 300px;
  padding: 8px 11px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(58, 107, 200, 0.35);
  box-shadow: 0 10px 28px rgba(17, 24, 39, 0.22), 0 3px 8px rgba(17, 24, 39, 0.14);
  transform-origin: top left;
  animation: epcCalloutIn 0.14s ease;
}

.epc-callout-badge {
  flex: none;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #3a6bc8;
  background: #e8eef8;
  padding: 2px 6px;
  border-radius: 5px;
  line-height: 1;
}

.epc-callout-name {
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}

.epc-callout-activity {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-callout-activity-label {
  color: #9aa2ad;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 5px;
}

.epc-callout-activity-value {
  color: #4b5563;
  font-weight: 500;
}

/* Barras de avance */
.epc-row {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 6px;
}

.epc-bar {
  flex: 1;
  height: 5px;
  background: #eef0f4;
  border-radius: 999px;
  overflow: hidden;
}

.epc-bar-sm { height: 4px; }

.epc-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #3a6bc8, #5a84d8);
  border-radius: 999px;
}

.epc-pct {
  font-size: 11px;
  font-weight: 700;
  color: #3a6bc8;
  min-width: 32px;
  text-align: right;
}

.epc-pct-sm {
  font-size: 10.5px;
  font-weight: 600;
  color: #4b5563;
  min-width: 30px;
}

.epc-layers {
  margin-top: 7px;
  padding-top: 7px;
  border-top: 1px solid rgba(17, 24, 39, 0.06);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.epc-layer-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

.epc-layer-label {
  width: 70px;
  flex: none;
  font-size: 10.5px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes epcCalloutIn {
  from { opacity: 0; transform: translateY(-3px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
